import { useState, useRef, useCallback, useEffect, type ReactNode } from 'react';
import { MoreVertical } from 'lucide-react';

interface DropdownItem {
  label: string;
  icon?: ReactNode;
  onClick: () => void;
  danger?: boolean;
}

interface DropdownMenuProps {
  items: DropdownItem[];
}

export function DropdownMenu({ items }: DropdownMenuProps) {
  const [open, setOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const menuRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const closeMenu = useCallback(() => {
    setOpen(false);
    setFocusedIndex(-1);
  }, []);

  useEffect(() => {
    if (open && focusedIndex >= 0 && itemRefs.current[focusedIndex]) {
      itemRefs.current[focusedIndex]?.focus();
    }
  }, [open, focusedIndex]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!open) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setFocusedIndex((prev) => (prev + 1) % items.length);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setFocusedIndex((prev) => (prev - 1 + items.length) % items.length);
          break;
        case 'Escape':
          e.preventDefault();
          closeMenu();
          break;
        case 'Home':
          e.preventDefault();
          setFocusedIndex(0);
          break;
        case 'End':
          e.preventDefault();
          setFocusedIndex(items.length - 1);
          break;
      }
    },
    [open, items.length, closeMenu],
  );

  return (
    <div className="relative" onKeyDown={handleKeyDown}>
      <button
        onClick={(e) => {
          e.stopPropagation();
          if (open) {
            closeMenu();
          } else {
            setOpen(true);
            setFocusedIndex(0);
          }
        }}
        aria-haspopup="true"
        aria-expanded={open}
        className="rounded-lg p-1.5 text-text-muted hover:bg-bg-hover hover:text-text-primary"
      >
        <MoreVertical className="h-4 w-4" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={closeMenu} />
          <div
            ref={menuRef}
            role="menu"
            className="absolute end-0 top-full z-20 mt-1 w-44 rounded-lg border border-border-default bg-bg-secondary shadow-xl"
          >
            {items.map((item, i) => (
              <button
                key={i}
                ref={(el) => { itemRefs.current[i] = el; }}
                role="menuitem"
                tabIndex={focusedIndex === i ? 0 : -1}
                onClick={() => { item.onClick(); closeMenu(); }}
                className={`flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-bg-hover ${item.danger ? 'text-danger' : ''}`}
              >
                {item.icon}
                {item.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
